// resize-observer-utils.ts – Debounced ResizeObserver helper
// Wraps ResizeObserver callbacks in requestAnimationFrame to prevent
// "ResizeObserver loop completed with undelivered notifications" errors
// that occur when many charts resize simultaneously on dashboard pages.

/**
 * Creates a ResizeObserver that coalesces rapid-fire resize events into
 * a single callback per animation frame. This prevents the browser error
 * "ResizeObserver loop completed with undelivered notifications" which
 * happens when an observer callback triggers a layout change that fires
 * another observation in the same frame.
 *
 * @param callback - receives the latest ResizeObserverEntry on each frame
 * @returns a standard ResizeObserver that can be observed/disconnected normally
 */
export function createDebouncedResizeObserver(
  callback: (entry: ResizeObserverEntry) => void
): ResizeObserver {
  let rafId: number | null = null;
  let latestEntry: ResizeObserverEntry | null = null;
  let isDisconnected = false;

  const observer = new ResizeObserver((entries) => {
    // Guard against callbacks queued before disconnect completed
    if (isDisconnected) return;

    // Capture the last entry (handles multi-element observation correctly)
    latestEntry = entries[entries.length - 1] ?? null;

    // If a rAF is already scheduled, skip — it will use the latest entry
    if (rafId !== null) return;

    rafId = requestAnimationFrame(() => {
      rafId = null;
      if (latestEntry && !isDisconnected) {
        callback(latestEntry);
      }
    });
  });

  // Patch disconnect to also cancel any pending rAF
  const originalDisconnect = observer.disconnect.bind(observer);
  observer.disconnect = () => {
    isDisconnected = true;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    latestEntry = null;
    originalDisconnect();
  };

  return observer;
}
